import { Download, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit, canView } from '../../utils/permissions';
import {
  getBankImports,
  uploadBankFiles,
  downloadImportSample,
  type BankImportDef,
  type BankFileUploadResult,
} from '../../api/bankFiles';
import { t } from '../../i18n/t';
import { showErrorToast } from '../../components/ErrorToast';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import './ForeignContractsPage.css';
import { LopDateInput } from '../../components/LopDateInput';

// Foreign-bank contract ingestion (יתרות מבנקים זרים) — the Bank Leumi (בל"ל)
// contracts workbook. A dedicated, single-file upload screen: it runs AFTER
// day-open and lands directly in Inventory, so it is NOT part of the daily
// Israeli-bank balance screen.
export function ForeignContractsPage() {
  const { user } = useAuth();
  const mayView = canView(user, 'bankFiles');
  const mayEdit = canEdit(user, 'bankFiles');

  const [def, setDef] = useState<BankImportDef | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [valueDate, setValueDate] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BankFileUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getBankImports()
      .then((defs) => {
        if (cancelled) return;
        const contract = defs.find((d) => d.isActive && d.importType === 'contracts');
        setDef(contract ?? null);
        if (!contract) setLoadError(t('foreignContracts.noDef'));
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onUpload = useCallback(async () => {
    if (!def || !file) return;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const res = await uploadBankFiles(
        def.bankId,
        [file],
        def.importId,
        valueDate || undefined,
      );
      setResult(res);
    } catch (e: unknown) {
      showErrorToast(e, { title: t('errorToast.titleOperation') });
    } finally {
      setBusy(false);
    }
  }, [def, file, valueDate]);

  const onSample = useCallback(async () => {
    if (!def) return;
    try {
      await downloadImportSample(def.importId);
    } catch (e: unknown) {
      showErrorToast(e, { title: t('errorToast.titleOperation') });
    }
  }, [def]);

  const succeeded = useMemo(
    () => result != null && result.filesLoaded > 0,
    [result],
  );

  if (!mayView) {
    return <div className="foreign-contracts">{t('foreignContracts.noAccess')}</div>;
  }

  return (
    <div className="foreign-contracts">
      <h1 className="foreign-contracts__title">{t('foreignContracts.title')}</h1>
      <p className="foreign-contracts__subtitle">{t('foreignContracts.subtitle')}</p>

      {loadError && <div className="foreign-contracts__error">{loadError}</div>}

      {def && (
        <div className="foreign-contracts__card">
          <div className="foreign-contracts__card-head">
            <strong>{def.importName}</strong>
            <LopButton variant="link" icon={<Download size={14} />} onClick={onSample}>
              {t('foreignContracts.sample')}
            </LopButton>
          </div>

          <div className="foreign-contracts__fields">
            <div className="foreign-contracts__field">
              <span className="foreign-contracts__label">{t('foreignContracts.file')}</span>
              <LopFilePicker
                id="foreign-contracts-file"
                accept=".xlsx,.zip"
                files={file ? [file] : []}
                disabled={!mayEdit || busy}
                onChange={(files) => {
                  setFile(files[0] ?? null);
                  setResult(null);
                  setError(null);
                }}
                hint=".xlsx / .zip"
              />
            </div>

            <div className="foreign-contracts__field">
              <label className="foreign-contracts__label" htmlFor="foreign-contracts-date">
                {t('foreignContracts.valueDate')}
              </label>
              <LopDateInput
                id="foreign-contracts-date"
                className="foreign-contracts__date"
                value={valueDate}
                disabled={!mayEdit || busy}
                onChange={setValueDate}
              />
              <span className="foreign-contracts__hint">{t('foreignContracts.valueDateHint')}</span>
            </div>

            <div>
              <LopButton
                variant="primary"
                icon={<Upload size={14} />}
                onClick={onUpload}
                loading={busy}
                disabled={!mayEdit || !file}
              >
                {t('foreignContracts.upload')}
              </LopButton>
            </div>
          </div>
        </div>
      )}

      {error && <div className="foreign-contracts__error">{error}</div>}

      {result && (
        <div className={`foreign-contracts__card ${succeeded ? 'foreign-contracts__card--ok' : 'foreign-contracts__card--warn'}`}>
          <strong>
            {succeeded
              ? t('foreignContracts.done')
              : t('foreignContracts.nothingLoaded')}
          </strong>
          {result.valueDates.length > 0 && (
            <div className="foreign-contracts__hint">
              {t('foreignContracts.valueDatesLabel')}: {result.valueDates.join(', ')}
            </div>
          )}
          {result.messages.length > 0 && (
            <ul className="foreign-contracts__messages">
              {result.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
